import { useEffect, useState } from 'react';
import { API_BASE } from '@/api/apiBase';

export interface CategoryGroupCategory {
  id: string;
  name: string;
}

export interface CategoryGroup {
  id: string;
  label: string;
  description?: string;
  enabledByDefault: boolean;
  categories: Array<CategoryGroupCategory>;
}

// Module-level cache: the catalog is static per worker deploy, so we only
// fetch it once per page load.
let cached: Array<CategoryGroup> | null = null;
let inFlight: Promise<Array<CategoryGroup>> | null = null;

const fetchGroups = async (): Promise<Array<CategoryGroup>> => {
  if (cached) return cached;
  if (inFlight) return inFlight;
  inFlight = (async () => {
    const response = await fetch(`${API_BASE}/api/categories`);
    if (!response.ok) throw new Error(`Failed to load category groups: ${response.status}`);
    const data = (await response.json()) as { groups: Array<CategoryGroup> };
    cached = data.groups;
    inFlight = null;
    return data.groups;
  })();
  return inFlight;
};

export const useCategoryGroups = (): {
  groups: Array<CategoryGroup> | null;
  isLoading: boolean;
  error: string | null;
} => {
  const [groups, setGroups] = useState<Array<CategoryGroup> | null>(cached);
  const [isLoading, setIsLoading] = useState(!cached);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cached) return;
    let cancelled = false;
    setIsLoading(true);
    fetchGroups()
      .then((g) => {
        if (!cancelled) setGroups(g);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load categories');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { groups, isLoading, error };
};
